import { FIELD_TYPES, type CollectionData, type RecordData } from '@tomo/core';
import { Avatar, AvatarStack, Button, IconButton, Menu, Textarea, toast, Tooltip, useConfirm, type MenuItem } from '@tomo/ui';
import { ArchiveRestore, Copy, Link2, MoreHorizontal, Plus, Rocket, Settings2, Shapes, Trash2 } from 'lucide-react';
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useCollection, useCollections, usePersonMap, usePresence, useProject, useReadOnly, useRecord, useSession } from '../../data/hooks.tsx';
import { Icon } from '../../lib/icons.tsx';
import { relativeTime } from '../../lib/time.ts';
import { RecordCover } from '../assets/RecordCover.tsx';
import { FieldTypeIcon } from '../fields/display.tsx';
import { FieldCell } from '../fields/FieldCell.tsx';
import { AddFieldPopover, FieldSettingsDialog } from '../fields/FieldSettings.tsx';
import { IconPicker } from '../shell/IconPicker.tsx';
import { RelatedPanel } from './RelatedPanel.tsx';
import './records.css';
import { itemNoun } from '../collections/view-utils.ts';
import { useOpenRecord } from '../windows/hooks.ts';

// The editor (TipTap + ProseMirror) is the heaviest dependency: load it with the first record.
const RichEditor = lazy(() => import('../editor/RichEditor.tsx').then((m) => ({ default: m.RichEditor })));

/** Where a record shows: its own page, or a window over the page. */
export type RecordMode = 'page' | 'window';

export function RecordView({ recordId, mode }: { recordId: string; mode: RecordMode }) {
  const record = useRecord(recordId);
  const collection = useCollection(record?.collectionId);
  if (!record || !collection) {
    return (
      <div className="record-missing">
        <p>This record doesn’t exist anymore.</p>
      </div>
    );
  }
  return <RecordBody key={record.id} record={record} collection={collection} mode={mode} />;
}

function RecordBody({ record, collection, mode }: { record: RecordData; collection: CollectionData; mode: RecordMode }) {
  const project = useProject();
  const readOnly = useReadOnly();
  const [settingsField, setSettingsField] = useState<string | null>(null);
  const fresh = !record.title && Date.now() - record.createdAt < 15_000;
  const body = project.getBody(record.id);

  return (
    <article className="record" data-mode={mode}>
      {record.deletedAt && <TrashBanner record={record} mode={mode} readOnly={readOnly} />}
      <RecordCover record={record} readOnly={readOnly} />
      <div className="record__layout">
        <div className="record__main">
          <header className="record__header">
            {readOnly ? (
              record.icon && (
                <span className="record__icon">
                  <Icon token={record.icon} size={38} />
                </span>
              )
            ) : (
              <IconPicker
                value={record.icon}
                allowClear
                onChange={(icon) => project.updateRecord(record.id, { icon })}
                trigger={
                  record.icon ? (
                    <button type="button" className="record__icon" aria-label="Change icon">
                      <Icon token={record.icon} size={38} />
                    </button>
                  ) : (
                    <button type="button" className="record__add-icon">
                      <Shapes size={14} /> Add icon
                    </button>
                  )
                }
              />
            )}
            <TitleInput record={record} collection={collection} autoFocus={fresh} readOnly={readOnly} />
            <RecordMeta record={record} collection={collection} mode={mode} readOnly={readOnly} />
          </header>

          <section className="props" aria-label="Properties">
            {collection.fields.map((f) => (
              <div key={f.id} className="prop" data-type={f.type}>
                <Tooltip content={f.description || FIELD_TYPES[f.type].label} placement="left">
                  <button type="button" className="prop__label" onClick={() => setSettingsField(f.id)}>
                    <FieldTypeIcon type={f.type} />
                    <span className="g-truncate">{f.name}</span>
                  </button>
                </Tooltip>
                <div className="prop__value">
                  <FieldCell record={record} field={f} collection={collection} variant="panel" readOnly={readOnly} />
                </div>
              </div>
            ))}
            {!readOnly && (
              <AddFieldPopover
                collection={collection}
                onCreated={(id, needsSetup) => needsSetup && setSettingsField(id)}
                trigger={
                  <button type="button" className="props__add">
                    <Plus size={14} /> Add a field
                  </button>
                }
              />
            )}
          </section>

          <div className="record__rule" />

          {body && (
            <Suspense fallback={<div className="editor editor--loading" aria-busy="true" />}>
              <RichEditor recordId={record.id} fragment={body} readOnly={readOnly} />
            </Suspense>
          )}
        </div>
        <aside className="record__aside">
          <RelatedPanel record={record} collection={collection} />
        </aside>
      </div>
      <FieldSettingsDialog collection={collection} fieldId={settingsField} onClose={() => setSettingsField(null)} />
    </article>
  );
}

function TitleInput({ record, collection, autoFocus, readOnly }: { record: RecordData; collection: CollectionData; autoFocus: boolean; readOnly: boolean }) {
  const project = useProject();
  const [value, setValue] = useState(record.title);
  const focused = useRef(false);
  const ref = useRef<HTMLTextAreaElement | null>(null);
  // Follow remote renames unless the user is typing.
  useEffect(() => {
    if (!focused.current) setValue(record.title);
  }, [record.title]);
  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);
  return (
    <Textarea
      ref={ref}
      variant="ghost"
      autoGrow
      rows={1}
      className="record__title"
      placeholder={`Untitled ${itemNoun(collection)}`}
      value={value}
      readOnly={readOnly}
      aria-label="Title"
      onFocus={() => (focused.current = true)}
      onBlur={() => {
        focused.current = false;
        if (value.trim() !== record.title) project.updateRecord(record.id, { title: value.trim() });
      }}
      onChange={(e) => {
        const v = e.target.value.replace(/\n/g, ' ');
        setValue(v);
        project.updateRecord(record.id, { title: v });
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          // This record's editor: other windows have their own.
          (e.currentTarget.closest('.record')?.querySelector('.prose') as HTMLElement | null)?.focus();
        }
      }}
    />
  );
}

function RecordMeta({ record, collection, mode, readOnly }: { record: RecordData; collection: CollectionData; mode: RecordMode; readOnly: boolean }) {
  const project = useProject();
  const session = useSession();
  const people = usePersonMap();
  const presence = usePresence();
  const collections = useCollections();
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const confirm = useConfirm();
  const author = record.createdBy ? people.get(record.createdBy) : undefined;
  const editor = record.updatedBy ? people.get(record.updatedBy) : undefined;
  const here = useMemo(() => {
    const list = presence.filter((p) => !p.self && p.location === record.id);
    return [...new Map(list.map((p) => [p.id, p])).values()];
  }, [presence, record.id]);

  /**
   * Make a record of this one in another collection, linked back through a relation field if that
   * collection has one. It replaces promoting an idea to a task, and works between any two.
   */
  const sendTo = (target: CollectionData) => {
    const related = target.fields.find((f) => f.type === 'relation');
    const id = project.createRecord(target.id, { title: record.title, values: related ? { [related.id]: [record.id] } : {} });
    toast.success(`Sent to ${target.name}`, { action: { label: 'Open', onClick: () => openRecord(id) } });
  };

  const targets = collections.filter((c) => c.id !== collection.id);
  const items: MenuItem[] = [
    ...(!readOnly && targets.length
      ? [
          { type: 'label', label: 'Send to' } as MenuItem,
          ...targets.map<MenuItem>((c) => ({ key: `send-${c.id}`, label: c.name, icon: <Icon token={c.icon} />, onSelect: () => sendTo(c) })),
          { type: 'separator' } as MenuItem,
        ]
      : []),
    ...(readOnly
      ? []
      : [
          {
            label: 'Duplicate',
            icon: <Copy />,
            onSelect: () => {
              const id = project.duplicateRecord(record.id);
              if (id) openRecord(id);
            },
          } as MenuItem,
        ]),
    {
      label: 'Copy link',
      icon: <Link2 />,
      onSelect: () => {
        void navigator.clipboard?.writeText(`${window.location.origin}/p/${session.id}/r/${record.id}`);
        toast.success('Link copied');
      },
    },
    { label: `Go to ${collection.name}`, icon: <Settings2 />, onSelect: () => navigate(`/p/${session.id}/c/${collection.id}`) },
  ];
  if (!readOnly) items.push(
    { type: 'separator' },
    {
      label: 'Move to trash',
      icon: <Trash2 />,
      danger: true,
      onSelect: async () => {
        if (await confirm({ title: 'Move to trash?', description: 'You can restore it from Trash at any time.', confirmLabel: 'Move to trash', danger: true })) {
          project.trashRecord(record.id);
          if (mode === 'page') navigate(`/p/${session.id}/c/${collection.id}`);
        }
      },
    },
  );

  return (
    <div className="record__meta">
      <Link to={`/p/${session.id}/c/${collection.id}`} className="record__collection" data-color={collection.color}>
        <Icon token={collection.icon} size={13} />
        {collection.name}
      </Link>
      {author && (
        <span className="record__meta-item">
          <Avatar name={author.name} color={author.color} size={16} /> {author.name}
        </span>
      )}
      <span className="record__meta-item" title={new Date(record.updatedAt).toLocaleString()}>
        Edited {relativeTime(record.updatedAt)}
        {editor && editor.id !== author?.id ? ` by ${editor.name}` : ''}
      </span>
      <span className="g-spacer" />
      {here.length > 0 && (
        <Tooltip content={`${here.map((p) => p.name).join(', ')} ${here.length === 1 ? 'is' : 'are'} here`}>
          <span>
            <AvatarStack people={here} size={22} ring />
          </span>
        </Tooltip>
      )}
      <Menu
        placement="bottom-end"
        trigger={
          <IconButton label="More actions" size="sm">
            <MoreHorizontal />
          </IconButton>
        }
        items={items}
      />
    </div>
  );
}

function TrashBanner({ record, mode, readOnly }: { record: RecordData; mode: RecordMode; readOnly: boolean }) {
  const project = useProject();
  const confirm = useConfirm();
  const session = useSession();
  const navigate = useNavigate();
  return (
    <div className="trash-banner">
      <Trash2 size={15} />
      <span>This record is in the trash.</span>
      {!readOnly && (
        <>
          <Button size="sm" variant="secondary" icon={<ArchiveRestore />} onClick={() => project.restoreRecord(record.id)}>
            Restore
          </Button>
          <Button
            size="sm"
            variant="danger-soft"
            onClick={async () => {
              if (await confirm({ title: 'Delete forever?', description: 'This cannot be undone once you leave the page.', confirmLabel: 'Delete forever', danger: true })) {
                project.destroyRecord(record.id);
                // A window closes by itself once its record is gone.
                if (mode === 'page') navigate(`/p/${session.id}/trash`);
              }
            }}
          >
            Delete forever
          </Button>
        </>
      )}
    </div>
  );
}
